import { supabase } from '../supabase';
import { offlineStorage, type PendingReport } from './offlineStorage';

const MAX_RETRY_COUNT = 3;

export class SyncEngine {
  private isSyncing = false;
  private syncCallbacks: Set<(count: number) => void> = new Set();
  private statusCallbacks: Set<(status: string) => void> = new Set();

  onPendingCountChange(callback: (count: number) => void): () => void {
    this.syncCallbacks.add(callback);
    return () => this.syncCallbacks.delete(callback);
  }

  onStatusChange(callback: (status: string) => void): () => void {
    this.statusCallbacks.add(callback);
    return () => this.statusCallbacks.delete(callback);
  }

  private notifyPendingCount(count: number): void {
    this.syncCallbacks.forEach(callback => callback(count));
  }

  private notifyStatus(status: string): void {
    this.statusCallbacks.forEach(callback => callback(status));
  }

  async syncAll(): Promise<void> {
    if (this.isSyncing) {
      console.log('Sync already in progress, skipping...');
      return;
    }

    if (!navigator.onLine) {
      console.log('Device is offline, skipping sync...');
      return;
    }

    this.isSyncing = true;
    console.log('Starting sync process...');

    try {
      const pendingReports = await offlineStorage.getPendingReports();

      if (pendingReports.length === 0) {
        console.log('No pending reports to sync');
        this.notifyPendingCount(0);
        return;
      }

      console.log(`Found ${pendingReports.length} pending reports`);
      this.notifyStatus(`Syncing ${pendingReports.length} report${pendingReports.length === 1 ? '' : 's'}...`);

      const sortedReports = pendingReports.sort(
        (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
      );

      let successCount = 0;
      let failCount = 0;

      for (const report of sortedReports) {
        try {
          await this.syncSingleReport(report);
          await offlineStorage.deleteReport(report.id);
          successCount++;

          const remaining = await offlineStorage.getPendingReports();
          this.notifyPendingCount(remaining.length);

          if (remaining.length > 0) {
            this.notifyStatus(`Syncing... ${remaining.length} remaining`);
          }
        } catch (error) {
          console.error(`Failed to sync report ${report.id}:`, error);
          failCount++;

          const newRetryCount = report.retry_count + 1;
          if (newRetryCount >= MAX_RETRY_COUNT) {
            await offlineStorage.updateReportStatus(report.id, 'failed', newRetryCount);
          } else {
            await offlineStorage.updateReportStatus(report.id, 'pending', newRetryCount);
          }
        }
      }

      const remaining = await offlineStorage.getPendingReports();
      this.notifyPendingCount(remaining.length);

      if (successCount > 0) {
        this.notifyStatus(`Successfully synced ${successCount} report${successCount === 1 ? '' : 's'}!`);
      }

      if (failCount > 0) {
        this.notifyStatus(`${failCount} report${failCount === 1 ? '' : 's'} failed to sync. Will retry later.`);
      }

      await offlineStorage.clearOldFailedReports(7);

    } catch (error) {
      console.error('Sync process error:', error);
      this.notifyStatus('Sync failed. Will retry automatically.');
    } finally {
      this.isSyncing = false;
      console.log('Sync process completed');
    }
  }

  private async syncSingleReport(report: PendingReport): Promise<void> {
    await offlineStorage.updateReportStatus(report.id, 'syncing');

    let photo_url: string | null = null;

    if (report.photo_blob) {
      const fileName = `${Date.now()}-${report.device_id}.jpg`;
      const { data, error } = await supabase.storage
        .from('verge-photos')
        .upload(fileName, report.photo_blob, {
          contentType: 'image/jpeg'
        });

      if (error) throw error;

      const { data: { publicUrl } } = supabase.storage
        .from('verge-photos')
        .getPublicUrl(fileName);

      photo_url = publicUrl;
    }

    const { error } = await supabase
      .from('verge_conditions')
      .insert([
        {
          grade: report.grade,
          latitude: report.latitude,
          longitude: report.longitude,
          timestamp: report.timestamp,
          street_name: report.street_name,
          local_authority: report.local_authority,
          device_id: report.device_id,
          photo_url,
          litter_left_side: report.litter_left_side ?? false,
          litter_right_side: report.litter_right_side ?? false,
          litter_central_reservation: report.litter_central_reservation ?? false,
          dangerous_debris: report.dangerous_debris ?? false,
          registration_plate_detected: report.registration_plate_detected ?? false,
        }
      ]);

    if (error) throw error;
  }

  async getPendingCount(): Promise<number> {
    const reports = await offlineStorage.getPendingReports();
    return reports.length;
  }
}

export const syncEngine = new SyncEngine();
